import { ActionType, Maybe, ReferenceMap } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { InputFormBlock } from "components/app/input-form-block";
import {
    ReferralSourcePicker,
    toMutationInput,
} from "components/references/select-reference/referral-source-picker";
import { trpc, trpcClient } from "components/rpc/trpc";
import { IsAllowed } from "components/shared/is-allowed";
import { AutosavingInput } from "components/ui/autosaving-input";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useMemo } from "react";

type HomeCareLeadDetail = inferProcedureOutput<AppRouter["homeCareLead"]["get"]>;

interface HomeCareLeadReferralSourceProps {
    homeCareLead: HomeCareLeadDetail;
    invalidate: () => void;
}

export const HomeCareLeadReferralSource: React.FC<
    HomeCareLeadReferralSourceProps
> = props => {
    const { homeCareLead, invalidate } = props;
    const trpcContext = trpc.useTRPC();

    const setReferralSource = useMutation({
        ...trpcContext.homeCareLead.setReferralSource.mutationOptions(),
        onSuccess: () => {
            invalidate();
        },
    });

    const referralSourceMap: ReferenceMap = useMemo(
        () =>
            ({
                identifier: Maybe.fromValue(homeCareLead.referralSourceToken).map(s =>
                    s.length > 0 ? s : null,
                ),
                business: Maybe.fromValue(homeCareLead.referralBusinessId),
                contact: Maybe.fromValue(homeCareLead.referralContactId),
            }) as ReferenceMap,
        [
            homeCareLead.referralSourceToken,
            homeCareLead.referralBusinessId,
            homeCareLead.referralContactId,
        ],
    );

    const showNotes = useMemo(() => {
        return (
            homeCareLead.referralSourceToken ||
            homeCareLead.referralContactId ||
            homeCareLead.referralBusinessId ||
            typeof homeCareLead.referralNotes === "string"
        );
    }, [homeCareLead]);

    const handleNotesCommit = async (newVal: string) => {
        await trpcClient.homeCareLead.update.mutate({
            id: homeCareLead.id,
            referralNotes: newVal,
        });
        invalidate();
    };

    return (
        <IsAllowed actionType={ActionType.ViewReference}>
            <InputFormBlock>
                <div className="mb-2">
                    <ReferralSourcePicker
                        createOptions={["contact"]}
                        variant={{
                            kind: "detailed",
                            parentEntityType: AdvancedSearchEntityType.HomeCareLead,
                            parentId: homeCareLead.id,
                        }}
                        clearing="allowed"
                        value={referralSourceMap}
                        onChange={(newValue: ReferenceMap) => {
                            setReferralSource.mutate({
                                homeCareLeadId: homeCareLead.id,
                                source: toMutationInput(newValue),
                            });
                        }}
                    />
                </div>
                {showNotes ? (
                    <div className="form-group">
                        <AutosavingInput
                            multiLine={true}
                            label="Referral Notes (optional)"
                            initial={homeCareLead.referralNotes}
                            onCommit={handleNotesCommit}
                        />
                    </div>
                ) : null}
            </InputFormBlock>
        </IsAllowed>
    );
};
